/**
 * Invoices.tsx
 * Invoice list page — stats bar, filter bar, table with status badges and row actions.
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import InvoiceBuilder from "./InvoiceBuilder";
import InvoiceDetail from "./InvoiceDetail";

interface StripeInvoice {
  id: string;
  number?: string;
  status: string;
  customerName?: string;
  customerEmail?: string;
  amountDue: number;
  dueDate?: string;
  created: string;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
}

interface LocalInvoiceRecord {
  id: number;
  invoiceNumber: string;
  status: string;
  customerName?: string;
  customerEmail: string;
  totalCents: number;
  amountPaidCents: number;
  invoiceDate: string;
  dueDate?: string;
  paidAt?: string;
  stripeInvoiceId?: string;
}

type StatusFilter = "all" | "draft" | "open" | "paid" | "void" | "uncollectible";
type Tab = "stripe" | "local";

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDate(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function isOverdue(invoice: StripeInvoice): boolean {
  if (invoice.status !== "open") return false;
  if (!invoice.dueDate) return false;
  return new Date(invoice.dueDate) < new Date();
}

function StatusBadge({ invoice }: { invoice: StripeInvoice }) {
  const overdue = isOverdue(invoice);
  const status = overdue ? "overdue" : invoice.status;
  const classes: Record<string, string> = {
    draft: "bg-slate-100 text-slate-600",
    open: "bg-blue-100 text-blue-700",
    overdue: "bg-orange-100 text-orange-700",
    paid: "bg-emerald-100 text-emerald-700",
    void: "bg-red-100 text-red-600",
    uncollectible: "bg-yellow-100 text-yellow-700",
  };
  const labels: Record<string, string> = {
    draft: "Draft",
    open: "Open",
    overdue: "Overdue",
    paid: "Paid",
    void: "Void",
    uncollectible: "Uncollectible",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        classes[status] ?? "bg-slate-100 text-slate-600"
      }`}
    >
      {labels[status] ?? status}
    </span>
  );
}

export default function Invoices() {
  const api = useApi();
  const toastAlert = useToastAlert();

  const [activeTab, setActiveTab] = useState<Tab>("stripe");
  const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [customerSearch, setCustomerSearch] = useState("");
  const [showBuilder, setShowBuilder] = useState(false);
  const [detailInvoiceId, setDetailInvoiceId] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  // Local invoice records
  const [localRecords, setLocalRecords] = useState<LocalInvoiceRecord[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [mailingId, setMailingId] = useState<number | null>(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (customerSearch.trim()) params.set("customerId", customerSearch.trim());
      const resp = await api.admin.get(`api/StripeInvoice?${params.toString()}`);
      if (resp.ok) {
        const data = await resp.json();
        setInvoices(Array.isArray(data) ? data : []);
      }
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin, statusFilter, customerSearch]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const fetchLocalRecords = useCallback(async () => {
    setLocalLoading(true);
    try {
      const resp = await api.admin.get("api/BillingInvoiceRecord");
      if (resp.ok) {
        const data = await resp.json();
        setLocalRecords(Array.isArray(data) ? data : []);
      }
    } catch {
      setLocalRecords([]);
    } finally {
      setLocalLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    if (activeTab === "local") fetchLocalRecords();
  }, [activeTab, fetchLocalRecords]);

  async function handleMailRecord(record: LocalInvoiceRecord) {
    setMailingId(record.id);
    try {
      const resp = await api.admin.post(`api/BillingInvoiceRecord/${record.id}/mail`, {});
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, `Invoice ${record.invoiceNumber} mailed to ${record.customerEmail}`);
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to mail invoice.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Network error.");
    } finally {
      setMailingId(null);
    }
  }

  const totalDue = invoices
    .filter((i) => i.status === "open")
    .reduce((sum, i) => sum + i.amountDue, 0);

  const overdueCount = invoices.filter(isOverdue).length;

  const now = new Date();
  const paidThisMonth = invoices
    .filter((i) => {
      if (i.status !== "paid") return false;
      if (!i.created) return false;
      const d = new Date(i.created);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    })
    .reduce((sum, i) => sum + i.amountDue, 0);

  async function handleSend(invoice: StripeInvoice) {
    setActing(invoice.id + "-send");
    try {
      const resp = await api.admin.post(`api/StripeInvoice/${invoice.id}/send`, {});
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Invoice sent.");
        fetchInvoices();
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to send invoice.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to send invoice.");
    } finally {
      setActing(null);
    }
  }

  async function handleVoid(invoice: StripeInvoice) {
    if (!confirm("Void this invoice? This cannot be undone.")) return;
    setActing(invoice.id + "-void");
    try {
      const resp = await api.admin.post(`api/StripeInvoice/${invoice.id}/void`, {});
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Invoice voided.");
        fetchInvoices();
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to void invoice.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to void invoice.");
    } finally {
      setActing(null);
    }
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="border-b border-slate-200 px-6 pt-5 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Invoices</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Create, track, and manage Stripe invoices.
            </p>
          </div>
          <button
            onClick={() => setShowBuilder(true)}
            className="px-4 py-2 rounded-md bg-cornflower-blue-600 text-white text-sm font-medium hover:bg-cornflower-blue-700 flex items-center gap-2"
          >
            <i className="fa-solid fa-plus" />
            New Invoice
          </button>
        </div>

        {/* Tab toggle */}
        <div className="flex gap-1 mt-4 border-b border-slate-200">
          {(["stripe", "local"] as Tab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                activeTab === tab
                  ? "border-cornflower-blue-500 text-cornflower-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab === "stripe" ? "Stripe Invoices" : "Local Records"}
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        {activeTab === "stripe" && (
          <>
            {/* Stats Bar */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-medium text-slate-500 uppercase">Total Due</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{formatCurrency(totalDue)}</p>
                <p className="text-xs text-slate-400 mt-0.5">Open invoices</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-medium text-slate-500 uppercase">Overdue</p>
                <p
                  className={`text-2xl font-bold mt-1 ${
                    overdueCount > 0 ? "text-orange-600" : "text-slate-800"
                  }`}
                >
                  {overdueCount}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Past due date</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-medium text-slate-500 uppercase">Paid This Month</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">
                  {formatCurrency(paidThisMonth)}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Collected</p>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="flex gap-3 items-center flex-wrap">
              <select
                className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="open">Open</option>
                <option value="paid">Paid</option>
                <option value="void">Void</option>
                <option value="uncollectible">Uncollectible</option>
              </select>
              <input
                type="text"
                className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500"
                placeholder="Filter by customer ID…"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
              />
              <button
                onClick={fetchInvoices}
                className="px-3 py-2 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1"
                aria-label="Refresh"
              >
                <i className="fa-solid fa-arrows-rotate" />
              </button>
            </div>

            {/* Stripe Invoices Table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <i className="fa-solid fa-spinner fa-spin text-slate-400 text-xl" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <i className="fa-solid fa-file-invoice text-4xl mb-3" />
                  <p className="text-sm">No invoices found.</p>
                  <button
                    onClick={() => setShowBuilder(true)}
                    className="mt-4 px-4 py-2 text-sm text-cornflower-blue-600 border border-cornflower-blue-200 rounded-md hover:bg-cornflower-blue-50"
                  >
                    Create your first invoice
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-[1fr_1fr_130px_110px_110px_150px] gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
                    <span>Customer</span>
                    <span>Invoice #</span>
                    <span className="text-right">Amount</span>
                    <span>Status</span>
                    <span>Due Date</span>
                    <span className="text-right">Actions</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {invoices.map((inv) => (
                      <div
                        key={inv.id}
                        className="grid grid-cols-[1fr_1fr_130px_110px_110px_150px] gap-3 px-4 py-3 items-center hover:bg-slate-50"
                      >
                        <div>
                          <p className="text-sm font-medium text-slate-800 truncate">
                            {inv.customerName ?? "—"}
                          </p>
                          <p className="text-xs text-slate-400 truncate">{inv.customerEmail}</p>
                        </div>
                        <span className="text-sm text-slate-600 truncate">
                          {inv.number ?? inv.id.slice(-8)}
                        </span>
                        <span className="text-sm font-medium text-slate-800 text-right tabular-nums">
                          {formatCurrency(inv.amountDue)}
                        </span>
                        <StatusBadge invoice={inv} />
                        <span className="text-sm text-slate-600">{formatDate(inv.dueDate)}</span>
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => setDetailInvoiceId(inv.id)}
                            className="p-1.5 rounded text-slate-400 hover:text-cornflower-blue-600 hover:bg-slate-100"
                            title="View details"
                          >
                            <i className="fa-solid fa-eye text-xs" />
                          </button>
                          {inv.status === "open" && (
                            <button
                              onClick={() => handleSend(inv)}
                              disabled={acting === inv.id + "-send"}
                              className="p-1.5 rounded text-slate-400 hover:text-cornflower-blue-600 hover:bg-slate-100 disabled:opacity-50"
                              title="Send invoice"
                            >
                              {acting === inv.id + "-send" ? (
                                <i className="fa-solid fa-spinner fa-spin text-xs" />
                              ) : (
                                <i className="fa-solid fa-paper-plane text-xs" />
                              )}
                            </button>
                          )}
                          {(inv.status === "open" || inv.status === "draft") && (
                            <button
                              onClick={() => handleVoid(inv)}
                              disabled={acting === inv.id + "-void"}
                              className="p-1.5 rounded text-slate-400 hover:text-red-500 hover:bg-slate-100 disabled:opacity-50"
                              title="Void invoice"
                            >
                              {acting === inv.id + "-void" ? (
                                <i className="fa-solid fa-spinner fa-spin text-xs" />
                              ) : (
                                <i className="fa-solid fa-ban text-xs" />
                              )}
                            </button>
                          )}
                          {inv.invoicePdf && (
                            <a
                              href={inv.invoicePdf}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                              title="Download PDF"
                            >
                              <i className="fa-solid fa-file-pdf text-xs" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {activeTab === "local" && (
          <>
            {/* Local Records toolbar */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Local invoice records — our sequential audit trail, independent of Stripe.
              </p>
              <button
                onClick={fetchLocalRecords}
                className="px-3 py-2 rounded-md border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1"
                aria-label="Refresh local records"
              >
                <i className="fa-solid fa-arrows-rotate" />
              </button>
            </div>

            {/* Local Records Table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {localLoading ? (
                <div className="flex items-center justify-center py-16">
                  <i className="fa-solid fa-spinner fa-spin text-slate-400 text-xl" />
                </div>
              ) : localRecords.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <i className="fa-solid fa-file-lines text-4xl mb-3" />
                  <p className="text-sm">No local invoice records found.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-[140px_1fr_120px_100px_100px_130px] gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs font-medium text-slate-500 uppercase">
                    <span>Invoice #</span>
                    <span>Customer</span>
                    <span className="text-right">Amount</span>
                    <span>Status</span>
                    <span>Invoice Date</span>
                    <span className="text-right">Actions</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {localRecords.map((rec) => {
                      const statusColors: Record<string, string> = {
                        draft: "bg-slate-100 text-slate-600",
                        open: "bg-blue-100 text-blue-700",
                        paid: "bg-emerald-100 text-emerald-700",
                        void: "bg-red-100 text-red-600",
                        overdue: "bg-orange-100 text-orange-700",
                      };
                      const isLocalOverdue =
                        rec.status === "open" &&
                        rec.dueDate != null &&
                        new Date(rec.dueDate) < new Date();
                      const displayStatus = isLocalOverdue ? "overdue" : rec.status;
                      const statusLabel =
                        displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1);

                      return (
                        <div
                          key={rec.id}
                          className="grid grid-cols-[140px_1fr_120px_100px_100px_130px] gap-3 px-4 py-3 items-center hover:bg-slate-50"
                        >
                          <span className="text-sm font-medium text-slate-700 truncate">
                            {rec.invoiceNumber}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {rec.customerName ?? "—"}
                            </p>
                            <p className="text-xs text-slate-400 truncate">{rec.customerEmail}</p>
                          </div>
                          <span className="text-sm font-medium text-slate-800 text-right tabular-nums">
                            {formatCurrency(rec.totalCents)}
                          </span>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              statusColors[displayStatus] ?? "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {statusLabel}
                          </span>
                          <span className="text-sm text-slate-600">
                            {formatDate(rec.invoiceDate)}
                          </span>
                          <div className="flex items-center gap-1 justify-end">
                            <a
                              href={`api/BillingInvoiceRecord/${rec.id}/pdf`}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                              title="Download PDF"
                            >
                              <i className="fa-solid fa-file-pdf text-xs" />
                            </a>
                            <button
                              onClick={() => handleMailRecord(rec)}
                              disabled={mailingId === rec.id}
                              className="p-1.5 rounded text-slate-400 hover:text-cornflower-blue-600 hover:bg-slate-100 disabled:opacity-50"
                              title="Mail invoice"
                            >
                              {mailingId === rec.id ? (
                                <i className="fa-solid fa-spinner fa-spin text-xs" />
                              ) : (
                                <i className="fa-solid fa-envelope text-xs" />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {showBuilder && (
        <InvoiceBuilder
          onClose={() => setShowBuilder(false)}
          onCreated={() => fetchInvoices()}
        />
      )}

      {detailInvoiceId && (
        <InvoiceDetail
          invoiceId={detailInvoiceId}
          onClose={() => setDetailInvoiceId(null)}
          onAction={() => fetchInvoices()}
        />
      )}
    </div>
  );
}
